import { useParams } from 'react-router-dom';

import { HighlightCanvas } from '../highlight-canvas';
import { BoundingBoxSelector, SelectionBox } from './bounding-box-selector';
import { TextLayer } from './text-layer';
import { useGetDocImagePresignedURL } from '@/api/queries/useGetDocImagePresignedURL';
import { useGetDocPageText } from '@/api/queries/useGetDocPageText';
import { DocViewerLoader } from '@/new/components/doc-viewer/doc-viewer-loader';
import { BoundingBox, BoundingBoxWithId } from '@/types';

interface ImagePageProps {
  pageNumber: number;
  top: number;
  // Page dimensions at zoom 1. The image / text / highlight stack renders at
  // this size and is zoomed with a single CSS transform, so a zoom change
  // never re-lays-out the (potentially very dense) text layer.
  baseDimensions: {
    height: number;
    width: number;
  };
  zoom: number;
  highlights: BoundingBoxWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;
  searchQuery?: string;
  focusedMatch?: { pageNumber: number; indexOnPage: number } | null;
  docId: string;
  isSelectionMode?: boolean;
  onSelectionComplete?: (
    boundingBox: BoundingBox,
    imageData: string,
    selectionBox: SelectionBox,
    pageNumber: number,
  ) => void;
  maintainSelection?: boolean;
  activeSelection?: { box: SelectionBox; pageNumber: number } | null;
}

export const ImagePage = ({
  pageNumber,
  top,
  baseDimensions,
  zoom,
  highlights,
  highlightedFactId,
  onHighlightClick,
  highlightedChunkId,
  searchQuery,
  focusedMatch,
  docId,
  isSelectionMode,
  onSelectionComplete,
  maintainSelection,
  activeSelection,
}: ImagePageProps) => {
  const { matterId } = useParams();
  const { data: imageUrl } = useGetDocImagePresignedURL(matterId, docId, pageNumber - 1);

  // Page text is fetched per page, so it only loads for the (buffered) visible
  // pages this component is mounted for — same windowing as the page images.
  const { data: ocrLines } = useGetDocPageText(matterId, docId, pageNumber - 1);

  const dimensions = {
    height: baseDimensions.height * zoom,
    width: baseDimensions.width * zoom,
  };

  return (
    <div
      key={pageNumber}
      className="absolute left-1/2 -translate-x-1/2 overflow-hidden bg-white shadow-lg/6"
      style={{
        top: top,
        height: dimensions.height,
        width: dimensions.width,
      }}
      data-page-number={pageNumber}
    >
      {imageUrl ? (
        <>
          <div
            className="absolute top-0 left-0 origin-top-left"
            style={{
              height: baseDimensions.height,
              width: baseDimensions.width,
              transform: `scale(${zoom})`,
            }}
          >
            <img src={imageUrl} alt={`PDF Page ${pageNumber}`} className="select-none" crossOrigin="anonymous" />

            <TextLayer
              lines={ocrLines ?? []}
              pageNumber={pageNumber}
              width={baseDimensions.width}
              height={baseDimensions.height}
              highlights={highlights}
              onHighlightClick={onHighlightClick}
              searchQuery={searchQuery}
              focusedMatchIndex={focusedMatch?.pageNumber === pageNumber ? focusedMatch.indexOnPage : null}
              // While snipping, hand pointer events to the BoundingBoxSelector rather than text layer
              disabled={isSelectionMode || (maintainSelection && activeSelection?.pageNumber === pageNumber)}
            />

            <HighlightCanvas
              highlights={highlights}
              pageNumber={pageNumber}
              width={baseDimensions.width}
              height={baseDimensions.height}
              highlightedFactId={highlightedFactId}
              onHighlightClick={onHighlightClick}
              highlightedChunkId={highlightedChunkId}
              // Visual only — the text layer above owns selection and clicks.
              interactive={false}
            />
          </div>

          {(isSelectionMode || (maintainSelection && activeSelection?.pageNumber === pageNumber)) &&
            onSelectionComplete && (
              <BoundingBoxSelector
                isActive={
                  (isSelectionMode || (maintainSelection && activeSelection?.pageNumber === pageNumber)) ?? false
                }
                pageNumber={pageNumber}
                pageWidth={dimensions.width}
                pageHeight={dimensions.height}
                onSelectionComplete={(boundingBox, imageData, selectionBox) =>
                  onSelectionComplete(boundingBox, imageData, selectionBox, pageNumber)
                }
                maintainSelection={maintainSelection}
                activeSelection={activeSelection?.pageNumber === pageNumber ? activeSelection.box : null}
              />
            )}
        </>
      ) : (
        <DocViewerLoader />
      )}
    </div>
  );
};
